/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Heart, Star, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { SkyScene3D } from "@/components/Scene3D";

import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const LEVELS = [
  { id: 1, name: "Level 1", desc: "Pemanasan!", ballSize: 50, gravity: 0.18, pushForce: -1.8, targetY: 2, obstacles: [], collectStars: 0 },
  { id: 2, name: "Level 2", desc: "Gravitasi naik!", ballSize: 45, gravity: 0.22, pushForce: -1.6, targetY: 1.5, obstacles: [], collectStars: 0 },
  { id: 3, name: "Level 3", desc: "Awas awan!", ballSize: 45, gravity: 0.24, pushForce: -1.6, targetY: 1.5, obstacles: [{ type: "cloud", y: 25, speed: 0.6 }, { type: "cloud", y: 50, speed: -0.5 }], collectStars: 0 },
  { id: 4, name: "Level 4", desc: "Balon kecil!", ballSize: 35, gravity: 0.26, pushForce: -1.5, targetY: 1, obstacles: [{ type: "cloud", y: 20, speed: 0.7 }, { type: "cloud", y: 45, speed: -0.6 }], collectStars: 0 },
  { id: 5, name: "Level 5", desc: "Kumpulkan 3 bintang!", ballSize: 35, gravity: 0.28, pushForce: -1.4, targetY: 1, obstacles: [{ type: "cloud", y: 15, speed: 0.6 }, { type: "cloud", y: 40, speed: -0.5 }, { type: "cloud", y: 60, speed: 0.7 }], collectStars: 3 },
];

const playPopSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 600;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  } catch (err) { logger.debug("game op failed", err); }
};

const playWinSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523, 659, 784, 1047].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = f;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.3);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.3);
    });
  } catch (err) { logger.debug("game op failed", err); }
};

const playLoseSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.5);
    osc.type = "sawtooth";
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch (err) { logger.debug("game op failed", err); }
};

const speakWin = (text) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = getVoiceLang();
    u.rate = 0.85;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const BalonTeman = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [currentLevel, setCurrentLevel] = useState(0);
  const [gameState, setGameState] = useState("menu"); // menu, playing, won, lost
  const [playerNames, setPlayerNames] = useState(() => {
    const saved = loadPlayerNames();
    return [saved[0] || "", saved[1] || ""];
  });
  const [ballX, setBallX] = useState(50);
  const [ballY, setBallY] = useState(60);
  const [ballVY, setBallVY] = useState(0);
  const [ballVX, setBallVX] = useState(0);
  const [lives, setLives] = useState(3);
  const [starsCollected, setStarsCollected] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [cloudPositions, setCloudPositions] = useState([]);
  const [floatingStars, setFloatingStars] = useState([]);
  const [pushEffect, setPushEffect] = useState(null);
  const gameRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);

  const level = LEVELS[currentLevel];

  const initLevel = useCallback(() => {
    setBallX(50);
    setBallY(60);
    setBallVY(0);
    setBallVX(0);
    setStarsCollected(0);
    setPushEffect(null);
    setCloudPositions(level.obstacles.map((o, i) => ({ ...o, x: 20 + i * 30, id: i })));
    if (level.collectStars > 0) {
      setFloatingStars(Array.from({ length: level.collectStars }, (_, i) => ({
        id: i, x: 20 + i * 25, y: 20 + i * 15, collected: false,
      })));
    } else {
      setFloatingStars([]);
    }
  }, [level]);

  const startLevel = (lvlIdx) => {
    savePlayerNames(playerNames);
    setCurrentLevel(lvlIdx);
    setLives(3);
    setGameState("playing");
  };

  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  useEffect(() => {
    if (gameState === "playing") initLevel();
  }, [gameState, currentLevel, initLevel]);

  // Game loop
  useEffect(() => {
    if (gameState !== "playing") return;

    const gameLoop = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = Math.min((timestamp - lastTimeRef.current) / 16.67, 3);
      lastTimeRef.current = timestamp;

      setBallVY(prev => prev + level.gravity * delta);
      setBallY(prev => {
        const newY = prev + ballVY * delta;
        // Check win - reached target
        if (newY <= level.targetY) {
          if (level.collectStars > 0 && starsCollected < level.collectStars) {
            return Math.max(level.targetY, newY);
          }
          setGameState("won");
          recordGameWon("balon");
          if (soundOn) {
            playWinSound();
            setTimeout(() => speakWin(`Great! ${p1} and ${p2} kalian kompak!`), 500);
          }
          return newY;
        }
        // Check lose - fell to bottom
        if (newY >= 95) {
          setLives(prev => {
            const newLives = prev - 1;
            if (newLives <= 0) {
              setGameState("lost");
              if (soundOn) {
                playLoseSound();
                setTimeout(() => speakWin(`Yah! ${p1} and ${p2} coba lagi ya!`), 500);
              }
            } else {
              initLevel();
            }
            return newLives;
          });
          return 60;
        }
        return newY;
      });

      setBallX(prev => {
        let newX = prev + ballVX * delta;
        if (newX < 5) newX = 5;
        if (newX > 95) newX = 95;
        return newX;
      });
      setBallVX(prev => prev * 0.98);

      // Cloud movement
      setCloudPositions(prev => prev.map(c => {
        let newX = c.x + c.speed * delta;
        if (newX > 90) newX = -15;
        if (newX < -15) newX = 90;
        return { ...c, x: newX };
      }));

      // Star collision
      if (level.collectStars > 0) {
        setFloatingStars(prev => prev.map(s => {
          if (s.collected) return s;
          const dx = Math.abs(ballX - s.x);
          const dy = Math.abs(ballY - s.y);
          if (dx < 8 && dy < 8) {
            setStarsCollected(p => p + 1);
            if (soundOn) playPopSound();
            return { ...s, collected: true };
          }
          return s;
        }));
      }

      rafRef.current = requestAnimationFrame(gameLoop);
    };

    rafRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTimeRef.current = 0;
    };
  }, [gameState, level, ballVY, ballX, ballY, starsCollected, soundOn, initLevel]);

  // Keyboard controls
  useEffect(() => {
    if (gameState !== "playing") return;
    const handleKey = (e) => {
      if (e.key === 'a' || e.key === 'A') {
        setBallVY(prev => prev + level.pushForce);
        setBallVX(prev => prev + 0.8);
        setPushEffect("left");
        setTimeout(() => setPushEffect(null), 200);
        if (soundOn) playPopSound();
      }
      if (e.key === 'l' || e.key === 'L') {
        setBallVY(prev => prev + level.pushForce);
        setBallVX(prev => prev - 0.8);
        setPushEffect("right");
        setTimeout(() => setPushEffect(null), 200);
        if (soundOn) playPopSound();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [gameState, level, soundOn]);

  const pushLeft = () => {
    if (gameState !== "playing") return;
    setBallVY(prev => prev + level.pushForce);
    setBallVX(prev => prev + 0.8);
    setPushEffect("left");
    setTimeout(() => setPushEffect(null), 200);
    if (soundOn) playPopSound();
  };

  const pushRight = () => {
    if (gameState !== "playing") return;
    setBallVY(prev => prev + level.pushForce);
    setBallVX(prev => prev - 0.8);
    setPushEffect("right");
    setTimeout(() => setPushEffect(null), 200);
    if (soundOn) playPopSound();
  };

  // MENU screen
  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="balon-teman-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ y: [0, -10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            🎈
          </motion.span>
          <h1 className="font-fredoka text-3xl sm:text-4xl text-gold font-bold mb-2">Balon Teman</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">
            Kerja sama jaga balon tetap melayang dan arahkan ke bintang!
          </p>
          <div className="glass-card rounded-xl p-3 mb-4 text-left">
            <p className="font-fredoka text-gold-light/70 text-xs mb-1 font-bold">Cara Bermain:</p>
            <p className="font-fredoka text-gold-light/60 text-xs">Desktop: Tekan <span className="text-gold font-bold">A</span> (dorong kiri) & <span className="text-gold font-bold">L</span> (dorong kanan)</p>
            <p className="font-fredoka text-gold-light/60 text-xs">Mobile: Tap tombol kiri & kanan di bawah layar</p>
          </div>

          {/* Player Names */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="font-fredoka text-cyan-400 text-xs font-bold block mb-1">Pemain 1 (A)</label>
              <input data-testid="balon-p1-name" type="text" value={playerNames[0]}
                onChange={(e) => setPlayerNames(prev => [e.target.value, prev[1]])}
                placeholder="Pemain 1" maxLength={15}
                className="w-full glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-cyan-400/50" />
            </div>
            <div>
              <label className="font-fredoka text-pink-400 text-xs font-bold block mb-1">Pemain 2 (L)</label>
              <input data-testid="balon-p2-name" type="text" value={playerNames[1]}
                onChange={(e) => setPlayerNames(prev => [prev[0], e.target.value])}
                placeholder="Pemain 2" maxLength={15}
                className="w-full glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-pink-400/50" />
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-2 w-full mb-4">
          {LEVELS.map((lvl, idx) => (
            <motion.div key={lvl.id} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: idx * 0.1 }}>
              <Button data-testid={`balon-level-${lvl.id}`} onClick={() => startLevel(idx)}
                className="w-full h-14 font-fredoka font-bold text-base rounded-xl justify-between px-4"
                style={{ background: `linear-gradient(135deg, hsl(${200 + idx * 20}, 70%, 55%), hsl(${200 + idx * 20}, 70%, 45%))` }}>
                <span className="flex items-center gap-2">
                  <span className="text-xl">🎈</span>
                  {lvl.name}
                </span>
                <span className="text-xs opacity-80">{lvl.desc}</span>
              </Button>
            </motion.div>
          ))}
        </div>

        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
          <Home className="w-4 h-4 mr-2" />Kembali ke Menu Utama
        </Button>
      </div>
    );
  }

  // WIN screen
  if (gameState === "won") {
    const isLastLevel = currentLevel >= LEVELS.length - 1;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="balon-teman-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300}
          colors={["#FFD700", "#87CEEB", "#FF69B4", "#98FB98", "#FFB347"]} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
          className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <motion.span className="text-6xl block mb-3" animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: 2 }}>
            🎉
          </motion.span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Hebat! {p1} & {p2} Kompak!</h2>
          <p className="font-fredoka text-gold-light/80 mb-1">{LEVELS[currentLevel].name} selesai!</p>
          <div className="flex justify-center gap-1 mb-4">
            {[0, 1, 2].map(i => (
              <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <div className="flex flex-col gap-2">
            {!isLastLevel && (
              <Button onClick={() => { setCurrentLevel(prev => prev + 1); setGameState("playing"); setLives(3); }}
                className="w-full font-fredoka font-bold text-base rounded-xl h-12 bg-gradient-to-r from-green-400 to-emerald-500"
                data-testid="balon-next-level">
                Level Berikutnya
              </Button>
            )}
            <Button onClick={() => { setGameState("playing"); setLives(3); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"
              data-testid="balon-play-again">
              <RotateCcw className="w-4 h-4 mr-2" />Main Lagi
            </Button>
            <Button onClick={() => setGameState("menu")} variant="outline"
              className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
              Pilih Level
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // LOST screen
  if (gameState === "lost") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="balon-teman-lose">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
          className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <motion.span className="text-6xl block mb-3">😢</motion.span>
          <h2 className="font-fredoka text-2xl text-gold-light font-bold mb-2">Balon Jatuh!</h2>
          <p className="font-fredoka text-gold-light/70 mb-4">Yuk coba lagi, pasti bisa!</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setGameState("playing"); setLives(3); }}
              className="w-full font-fredoka font-bold rounded-xl h-12 bg-gradient-to-r from-yellow-400 to-amber-500"
              data-testid="balon-retry">
              <RotateCcw className="w-4 h-4 mr-2" />Coba Lagi
            </Button>
            <Button onClick={() => setGameState("menu")} variant="outline"
              className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl">
              Pilih Level
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PLAYING screen
  return (
    <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-2" data-testid="balon-teman-game" ref={gameRef}>
      {/* Header */}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🎈 {LEVELS[currentLevel].name}</span>
          <div className="flex gap-0.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Heart key={i} className={`w-5 h-5 ${i < lives ? 'text-red-400 fill-red-400' : 'text-gray-600'}`} />
            ))}
          </div>
          {level.collectStars > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="font-fredoka text-gold text-xs font-bold">{starsCollected}/{level.collectStars}</span>
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-7 w-7 p-0">
            {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
          </Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold hover:bg-gold/10 h-7 px-2">
            <Home className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Game Area - 3D */}
      <div className="w-full rounded-2xl overflow-hidden relative border-2 border-sky-300/30 game-area-3d"
        style={{
          height: Math.min(winH - 180, 500),
          background: "linear-gradient(180deg, #87CEEB 0%, #B0E0E6 30%, #E0F7FA 60%, #98FB98 95%, #228B22 100%)",
        }}>
        {/* Three.js 3D Background */}
        <SkyScene3D />

        {/* Clouds (decorative) - animated drift */}
        <motion.div className="absolute top-[8%] text-4xl opacity-40 select-none"
          animate={{ x: [0, 30, 0] }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          style={{ left: '10%' }}>☁️</motion.div>
        <motion.div className="absolute top-[15%] text-3xl opacity-30 select-none"
          animate={{ x: [0, -25, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          style={{ right: '15%' }}>☁️</motion.div>
        <motion.div className="absolute top-[5%] text-2xl opacity-25 select-none"
          animate={{ x: [0, 20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          style={{ left: '50%' }}>☁️</motion.div>

        {/* Rainbow - gentle pulse */}
        <motion.div className="absolute top-[2%] right-[5%] text-5xl opacity-30 select-none"
          animate={{ opacity: [0.2, 0.4, 0.2], scale: [1, 1.05, 1] }}
          transition={{ duration: 4, repeat: Infinity }}>🌈</motion.div>

        {/* Flowers on ground */}
        <motion.div className="absolute bottom-[5%] left-[10%] text-xl select-none opacity-60"
          animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity }}>🌸</motion.div>
        <motion.div className="absolute bottom-[5%] left-[30%] text-lg select-none opacity-50"
          animate={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 4, repeat: Infinity }}>🌼</motion.div>
        <motion.div className="absolute bottom-[5%] right-[20%] text-xl select-none opacity-60"
          animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 3.5, repeat: Infinity }}>🌻</motion.div>

        {/* Target star */}
        <motion.div className="absolute left-1/2 -translate-x-1/2 text-4xl z-10 select-none"
          style={{ top: `${level.targetY - 3}%` }}
          animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}>
          ⭐
        </motion.div>

        {/* Obstacle clouds */}
        {cloudPositions.map(c => (
          <div key={c.id} className="absolute text-5xl opacity-50 select-none z-[3]"
            style={{ left: `${c.x}%`, top: `${c.y}%`, transition: "left 0.05s linear" }}>
            ☁️
          </div>
        ))}

        {/* Floating stars to collect */}
        {floatingStars.map(s => !s.collected && (
          <motion.div key={s.id} className="absolute text-3xl z-[4] select-none"
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
            animate={{ y: [0, -5, 0], rotate: [0, 15, -15, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: s.id * 0.3 }}>
            ⭐
          </motion.div>
        ))}

        {/* Balloon - 3D */}
        <motion.div className="absolute z-10 select-none emoji-3d"
          style={{
            left: `${ballX}%`,
            top: `${ballY}%`,
            transform: "translate(-50%, -50%)",
            fontSize: `${level.ballSize}px`,
            filter: pushEffect ? "brightness(1.3) drop-shadow(0 0 20px rgba(255,100,100,0.7))" : "drop-shadow(0 4px 12px rgba(0,0,0,0.25))",
            transition: "filter 0.15s",
          }}
          animate={{
            scaleX: pushEffect === "left" ? [1, 0.85, 1.1, 1] : pushEffect === "right" ? [1, 0.85, 1.1, 1] : [1, 1.02, 1],
            scaleY: pushEffect ? [1, 1.15, 0.9, 1] : [1, 0.98, 1],
            rotate: ballVX > 0.5 ? [0, 8, 0] : ballVX < -0.5 ? [0, -8, 0] : 0,
          }}
          transition={{ duration: pushEffect ? 0.25 : 2, repeat: pushEffect ? 0 : Infinity }}>
          🎈
          {/* String with swing */}
          <motion.div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0.5 h-5 bg-gray-400 opacity-60 origin-top"
            animate={{ rotate: [0, 3, -3, 0] }}
            transition={{ duration: 2, repeat: Infinity }} />
        </motion.div>

        {/* Push effects - enhanced with sparkles */}
        <AnimatePresence>
          {pushEffect === "left" && (
            <>
              <motion.div className="absolute text-2xl z-20 select-none"
                style={{ left: `${ballX - 8}%`, top: `${ballY}%` }}
                initial={{ opacity: 1, scale: 1 }} animate={{ opacity: 0, x: -30, scale: 0.3 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}>
                💨
              </motion.div>
              {[0, 1, 2].map(i => (
                <motion.div key={`sl-${i}`} className="absolute text-sm z-20 select-none"
                  style={{ left: `${ballX - 5}%`, top: `${ballY - 2 + i * 3}%` }}
                  initial={{ opacity: 1, scale: 1 }} animate={{ opacity: 0, x: -15 - i * 8, scale: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}>
                  ✨
                </motion.div>
              ))}
            </>
          )}
          {pushEffect === "right" && (
            <>
              <motion.div className="absolute text-2xl z-20 select-none"
                style={{ left: `${ballX + 5}%`, top: `${ballY}%` }}
                initial={{ opacity: 1, scale: 1 }} animate={{ opacity: 0, x: 30, scale: 0.3 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}>
                💨
              </motion.div>
              {[0, 1, 2].map(i => (
                <motion.div key={`sr-${i}`} className="absolute text-sm z-20 select-none"
                  style={{ left: `${ballX + 6}%`, top: `${ballY - 2 + i * 3}%` }}
                  initial={{ opacity: 1, scale: 1 }} animate={{ opacity: 0, x: 15 + i * 8, scale: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}>
                  ✨
                </motion.div>
              ))}
            </>
          )}
        </AnimatePresence>

        {/* Grass with swaying blades */}
        <div className="absolute bottom-0 left-0 right-0 h-[5%] bg-gradient-to-t from-green-700 to-green-500 opacity-80" />
        <div className="absolute bottom-[4%] left-0 right-0 flex justify-around pointer-events-none">
          {[0,1,2,3,4,5,6,7].map(i => (
            <motion.span key={i} className="text-lg opacity-40 select-none"
              animate={{ rotate: [0, 8, -8, 0] }}
              transition={{ duration: 2 + i * 0.3, repeat: Infinity }}>🌿</motion.span>
          ))}
        </div>
      </div>

      {/* Mobile Controls */}
      <div className="flex justify-between w-full mt-3 gap-4">
        <Button data-testid="balon-push-left" onClick={pushLeft}
          className="flex-1 h-16 sm:h-20 text-lg sm:text-xl font-fredoka font-bold rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 btn-3d select-none"
          style={{ touchAction: "manipulation" }}>
          👈 {p1} (A)
        </Button>
        <Button data-testid="balon-push-right" onClick={pushRight}
          className="flex-1 h-16 sm:h-20 text-lg sm:text-xl font-fredoka font-bold rounded-2xl bg-gradient-to-r from-pink-400 to-rose-500 btn-3d select-none"
          style={{ touchAction: "manipulation" }}>
          {p2} (L) 👉
        </Button>
      </div>
    </div>
  );
};

export default BalonTeman;
